import { compileRegex, formatDateByPattern } from '@vft/core/validation/format.js';
import { TYPES, classifyValue } from '@vft/core/audit/typeDetector.js';

const TYPE_AFFINITY = [
  { fieldTypeMatch: /email/, scores: { [TYPES.EMAIL]: 1.0, [TYPES.TEXT]: 0.2 } },
  { fieldTypeMatch: /date|datetime|timestamp/, scores: { [TYPES.DATE]: 1.0, [TYPES.TEXT]: 0.2 } },
  { fieldTypeMatch: /number|numeric|integer|decimal|float/, scores: { [TYPES.NUMBER]: 1.0, [TYPES.TEXT]: 0.1 } },
  { fieldTypeMatch: /bool/, scores: { [TYPES.BOOLEAN]: 1.0 } },
];

function typeAffinity(columnType, fieldType) {
  if (!fieldType || !columnType) return 0;
  const ft = String(fieldType).toLowerCase();
  for (const rule of TYPE_AFFINITY) {
    if (rule.fieldTypeMatch.test(ft)) {
      return rule.scores[columnType] || 0;
    }
  }
  return 0;
}

function getSampleValues(column) {
  const out = [];
  for (const s of column?.sample || []) {
    if (s == null) continue;
    const v = typeof s === 'object' ? s.value : s;
    if (v === null || v === undefined || v === '') continue;
    out.push(v);
  }
  return out;
}

function valueToString(value, format) {
  if (value instanceof Date && !isNaN(value)) {
    if (format?.datePattern) return formatDateByPattern(value, format.datePattern);
    return value.toISOString();
  }
  return String(value);
}

export function isPermissiveRegex(regExp) {
  if (!regExp || typeof regExp !== 'string') return false;
  if (/\\[pP]\{[LMNSP]\}/.test(regExp)) return true;
  if (/^\^?\.\*\$?$/.test(regExp)) return true;
  if (/^\^?\.\+\$?$/.test(regExp)) return true;
  return false;
}

const PERMISSIVE_PENALTY = 0.3;

export function contentMatch(column, field) {
  if (!column || !field) return { score: 0, kind: 'none' };
  const samples = getSampleValues(column);
  if (samples.length === 0) return { score: 0, kind: 'empty' };

  const format = field.validation?.format;

  if (format?.regExp) {
    const re = compileRegex(format.regExp);
    if (re) {
      let pass = 0;
      let withinLength = 0;
      for (const v of samples) {
        const str = valueToString(v, format);
        const lenOk =
          (format.lengthMin == null || str.length >= format.lengthMin) &&
          (format.lengthMax == null || str.length <= format.lengthMax);
        if (lenOk) withinLength += 1;
        if (re.test(str)) pass += 1;
      }
      const passRate = pass / samples.length;
      const lengthRate = withinLength / samples.length;
      const baseScore = Math.min(passRate, lengthRate);
      const permissive = isPermissiveRegex(format.regExp);
      return {
        score: permissive ? baseScore * PERMISSIVE_PENALTY : baseScore,
        passRate,
        lengthRate,
        permissive,
        kind: 'regex',
      };
    }
  }

  const fieldType = field.type;
  const colType = column.detectedType || classifyValue(samples[0]);
  const score = typeAffinity(colType, fieldType);
  return { score, kind: score > 0 ? 'type' : 'none' };
}
